import React, { useEffect, useState } from 'react';
import { Radio, RadioChangeEvent } from 'antd';
import { Button } from '../../../design-system';
import { useTheme } from '../../../providers/theme';
import { Sample } from '../../../@types/samples/types';
import { AnalysisMethod, SampleRunResult, StatusFilter } from '../types';
import {
  hasCapillaryData,
  hasComputedResult,
  hasAnyAnalysisData,
} from '../analysisCategory';
import SampleThumbnail from './SampleThumbnail';
import styles from '../MultiAnalysisPage.module.css';

const PAGE_SIZE = 24;

type Props = {
  samples: Sample[];
  results: Map<string, SampleRunResult>;
  checkedUids: Set<string>;
  preexistingUids: Set<string>;
  statusFilter: StatusFilter;
  statusCounts: { valid: number; warning: number; failed: number };
  totalCount: number;
  /** Cards per row (density control). */
  columns: number;
  /** Active batch methodology — drives thumbnail params/curves/tags. */
  method: AnalysisMethod;
  /** Y (Pc) axis scale for the mini charts. */
  chartScale: 'linear' | 'log';
  /** Editable Pce input value per sample. */
  pceValue: (uid: string) => string;
  onPceChange: (uid: string, value: string) => void;
  onFilterChange: (filter: StatusFilter) => void;
  onColumnsChange: (columns: number) => void;
  onScaleChange: (scale: 'linear' | 'log') => void;
  onToggleChecked: (uid: string) => void;
  onInspect: (uid: string) => void;
};

const COLUMN_OPTIONS = [3, 4];

const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'valid', label: 'Valid' },
  { value: 'warning', label: 'Review' },
  { value: 'failed', label: 'Failed' },
  { value: 'unreviewed', label: 'Unreviewed' },
];

/**
 * IP-style responsive grid of per-sample fit panels with status filter
 * chips. Renders in pages of PAGE_SIZE with a "Show more" control so very
 * large batches don't mount hundreds of cards at once.
 */
const ThumbnailGrid = ({
  samples,
  results,
  checkedUids,
  preexistingUids,
  statusFilter,
  statusCounts,
  totalCount,
  columns,
  method,
  chartScale,
  pceValue,
  onPceChange,
  onFilterChange,
  onColumnsChange,
  onScaleChange,
  onToggleChecked,
  onInspect,
}: Props) => {
  const { isDay } = useTheme();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [statusFilter, totalCount]);

  const filterCount = (filter: StatusFilter): number | null => {
    if (filter === 'all') return totalCount;
    if (filter === 'valid') return statusCounts.valid;
    if (filter === 'warning') return statusCounts.warning;
    if (filter === 'failed') return statusCounts.failed;
    return null;
  };

  const visible = samples.slice(0, visibleCount);

  return (
    <div className={styles.gridArea}>
      <div className={styles.filterRow} role="tablist" aria-label="Status filter">
        {FILTERS.map(({ value, label }) => {
          const count = filterCount(value);
          return (
            <button
              key={value}
              type="button"
              data-status={value}
              className={
                statusFilter === value
                  ? `${styles.filterChip} ${styles.filterChipActive}`
                  : styles.filterChip
              }
              onClick={() => onFilterChange(value)}
            >
              {label}
              {count != null ? ` (${count})` : ''}
            </button>
          );
        })}

        <div className={styles.viewControls}>
          <span className={styles.viewControlLabel}>Scale</span>
          <Radio.Group
            value={chartScale === 'log'}
            onChange={(e: RadioChangeEvent) =>
              onScaleChange(e.target.value ? 'log' : 'linear')
            }
            options={[
              { label: 'Linear', value: false },
              { label: 'Logarithmic', value: true },
            ]}
            optionType="button"
            buttonStyle="solid"
            size="small"
            className={isDay ? 'day-radio' : 'night-radio'}
          />

          <span className={styles.viewControlLabel}>Columns</span>
          <Radio.Group
            value={columns}
            onChange={(e: RadioChangeEvent) => onColumnsChange(e.target.value)}
            options={COLUMN_OPTIONS.map(option => ({
              label: String(option),
              value: option,
            }))}
            optionType="button"
            buttonStyle="solid"
            size="small"
            className={isDay ? 'day-radio' : 'night-radio'}
          />
        </div>
      </div>

      {samples.length === 0 ? (
        <p className={styles.noMatches}>No samples match this filter.</p>
      ) : (
        <div
          className={styles.grid}
          style={{
            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          }}
        >
          {visible.map(sample => {
            // Pc samples are tagged relative to the ACTIVE methodology
            // (Analyzed = that methodology already has a stored result);
            // Kr-only / no-data samples keep their intrinsic status.
            const methodResult =
              method === 'lambda'
                ? sample.analysis_values?.lambda_values
                : sample.analysis_values?.liao;
            const dataStatus = hasCapillaryData(sample)
              ? methodResult != null
                ? 'analyzed'
                : 'input-only'
              : hasComputedResult(sample)
              ? 'analyzed'
              : hasAnyAnalysisData(sample)
              ? 'input-only'
              : 'no-data';
            return (
              <SampleThumbnail
                key={sample.uid}
                sample={sample}
                result={results.get(sample.uid)}
                checked={checkedUids.has(sample.uid)}
                method={method}
                overwrites={preexistingUids.has(sample.uid)}
                dataStatus={dataStatus}
                logScale={chartScale === 'log'}
                pce={pceValue(sample.uid)}
                onPceChange={value => onPceChange(sample.uid, value)}
                onToggleChecked={onToggleChecked}
                onInspect={onInspect}
              />
            );
          })}
        </div>
      )}

      {samples.length > visibleCount ? (
        <div className={styles.showMoreRow}>
          <Button
            variant="secondary"
            onClick={() => setVisibleCount(count => count + PAGE_SIZE)}
          >
            Show more ({samples.length - visibleCount} remaining)
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default ThumbnailGrid;
